import { BugReport, GridViewOutlined } from "@mui/icons-material";
import { SvgIconProps } from "@mui/material";
import { useEffect, useState } from "react";
import { DocumentIcon } from "../../../components/icons";
import FigmaLinkIcon from "../../../components/icons/FigmaIcon";
import { PERMISSIONS } from "../../../constants/permissions";
import {
  AllCrewIcon,
  ConfigurationIcon,
  ContactIcon,
  DashboardIcon,
  EndpointRequestIcon,
  GearIcon,
  MemoryIcon,
  OperationsIcon,
  UserAdminIcon
} from "../../sidebar/icons";

export const getAllAppRoutes = (): string[] => {
  return [
    "/dashboard",
    "/tv-dashboard",
    "/sales",
    "/operations",
    "/bookings",
    "/bookings/funeral",
    "/bookings/event",
    "/bookings/conference",
    "/bookings/church",
    "/bookings/funeral-home",
    "/bookings/other",
    "/list-booking2",
    "/contacts",
    "/contacts/add-company-contact",
    "/contacts/add-individual",
    "/venues",
    "/venues/add",
    "/crew-management",
    "/crew-management/all-crew",
    "/crew-management/checklists",
    "/crew-management/crew-ratings",
    "/crew-management/rates",
    "/stream-management",
    "/stream-management/add",
    "/stream-management/ten-day-forecast",
    "/gear-management",
    "/settings",
    "/marketing",
    "/reports",
    "/configurations",
    "/configurations/manage-roles",
    "/configurations/upcoming-features",
    "/configurations/thumbnail-template-manager",
    "/configurations/package",
    "/configurations/venues-admin",
    "/configurations/tv-dashboard-templates",
    "/configurations/footer-links",
    "/configurations/proposal-cover-page",
    "/configurations/dashboard-widgets",
    "/configurations/share-messages",
    "/configurations/proposal-introduction-letter",
    "/configurations/testimonials",
    "/configurations/seo-management",
    "/configurations/components-manager",
    "/configurations/email-templates",
    "/user-management",
    "/proposals",
    "/proposals/proposals-builder",
    "/proposal/service-quotation",
    "/request-for-quote",
    "/client-portal-dashboard",
    "/memory-table",
    "/autolog",
    "/sales",
    "/changelog",
    "/bug-reports",
    "/endpoint-requests",
    "/endpoint-requests/frontend",
    "/endpoint-requests/backend",
    "/assign-crew",
    "/site-visit",
    "/sponsorships",
    "/streamfund",
    "/referrals",
    "/companies",
    "/companies/add",
    "/courses",
    "/courses/overview",
    "/courses/create-course",
    "/assessments",
    "/badges",
    "/youtube-channels",
    "/bulk-crew-assignment",
    "/memorial-pages",
    "/memorial-pages-dashboard",
    "/chema",
    "/memory-book-dashboard",
    "/sites",
    "muzukuru-academy",
  ];
};

export type AppItem = {
  label: string;
  icon: React.ComponentType<SvgIconProps>;
  route?: string;
  action?: "OPEN_TOOLS_DRAWER";
  color: string;
  children?: { label: string; route: string }[];
  isCustom?: boolean;
  permission?: string | string[];
};

export const apps: AppItem[] = [
  {
    label: "Dashboard",
    icon: DashboardIcon,
    route: "/Dashboard",
    color: "#4FD1C5",
  },
  {
    label: "Bookings",
    icon: DocumentIcon,
    color: "#4FD1C5",
    permission: PERMISSIONS.BOOKING_VIEW,
    children: [
      { label: "Funeral", route: "/bookings/funeral" },
      { label: "Conference", route: "/bookings/conference" },
      { label: "Church", route: "/bookings/church" },
      { label: "Funeral Home", route: "/bookings/funeral-home" },
      { label: "Other", route: "/bookings/other" },
    ],
  },
  {
    label: "Contacts",
    icon: ContactIcon,
    route: "/contacts",
    color: "#63B3ED",
    permission: PERMISSIONS.CONTACT_VIEW,
  },
  {
    label: "Venues",
    icon: ContactIcon,
    route: "/venues",
    color: "#718096",
    permission: PERMISSIONS.VENUE_VIEW,
  },
  {
    label: "All Crew",
    icon: AllCrewIcon,
    route: "/crew-management/all-crew",
    color: "#F6AD55",
    permission: PERMISSIONS.CREW_VIEW,
  },
  {
    label: "Streams",
    icon: OperationsIcon,
    route: "/stream-management",
    color: "#4299E1",
    permission: PERMISSIONS.STREAM_VIEW,
  },
  {
    label: "Requested Quotes",
    icon: DocumentIcon,
    route: "/request-for-quote",
    color: "#ED64A6",
    permission: PERMISSIONS.QUOTE_REQUEST_VIEW,
  },
  {
    label: "Memorial Pages",
    icon: DocumentIcon,
    route: "/memorial-pages-dashboard",
    color: "#48BB78",
    permission: PERMISSIONS.MEMORIAL_PAGES_VIEW,
  },
  {
    label: "Proposal",
    icon: DocumentIcon,
    route: "/proposals",
    color: "#F687B3",
    permission: PERMISSIONS.PROPOSAL_VIEW,
  },
  {
    label: "Figma Designs",
    icon: FigmaLinkIcon,
    route: "/figma-designs",
    color: "#9F7AEA",
    permission: PERMISSIONS.FIGMA_VIEW,
  },
  {
    label: "Tool and Assets",
    icon: GridViewOutlined,
    action: "OPEN_TOOLS_DRAWER" as const,
    color: "#10B981",
    permission: PERMISSIONS.CONFIGURATION_VIEW,
  },
  {
    label: "Configurations",
    icon: ConfigurationIcon,
    route: "/configurations",
    color: "#ED8936",
    permission: PERMISSIONS.CONFIGURATION_VIEW,
  },
  {
    label: "User Admin",
    icon: UserAdminIcon,
    route: "/user-management",
    color: "#667EEA",
    permission: PERMISSIONS.USER_MANAGEMENT_VIEW,
  },
  {
    label: "Endpoint Request",
    icon: EndpointRequestIcon,
    route: "/endpoint-requests",
    color: "#D53F8C",
    permission: [
      PERMISSIONS.ENDPOINT_REQUEST_ADMIN,
      PERMISSIONS.ENDPOINT_REQUEST_BACKEND,
      PERMISSIONS.ENDPOINT_REQUEST_FRONTEND_ADD_COMPONENT,
    ],
  },
  {
    label: "Bug Reports",
    icon: BugReport,
    route: "/bug-reports",
    color: "#e60707",
    permission: PERMISSIONS.BUG_VIEW,
  },
  {
    label: "Memory Table",
    icon: MemoryIcon,
    route: "/memory-table",
    color: "#FC8181",
    permission: PERMISSIONS.COMMENT_VIEW,
  },
  {
    label: "Gear Management",
    icon: GearIcon,
    route: "/gear-management",
    color: "#458bc5",
    permission: PERMISSIONS.EQUIPMENT_VIEW,
  },
];

export type StoredCustomApp = {
  label: string;
  route: string;
  color: string;
  iconKey: string;
};

const STORAGE_KEY = "app_launcher_custom_apps";

export function useCustomApps() {
  const [customApps, setCustomApps] = useState<StoredCustomApp[]>(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      return raw ? (JSON.parse(raw) as StoredCustomApp[]) : [];
    } catch {
      return [];
    }
  });

  

  const addCustomApp = (app: StoredCustomApp) => {
    setCustomApps((prev) => {
      const next = [...prev, app];
      localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
      return next;
    });
  };

  const removeCustomApp = (label: string) => {
    setCustomApps((prev) => {
      const next = prev.filter((a) => a.label !== label);
      localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
      return next;
    });
  };

  useEffect(() => {
    const handleStorage = (event: StorageEvent) => {
      if (event.key !== STORAGE_KEY) return;

      try {
        const newValue = event.newValue;
        if (!newValue) return;

        setCustomApps(JSON.parse(newValue));
      } catch {
        console.log("failed")
      }
    };

    window.addEventListener("storage", handleStorage);

    return () => {
      window.removeEventListener("storage", handleStorage);
    };
  }, []);

  return { customApps, addCustomApp, removeCustomApp };
}
